import React from "react";
import { motion } from "framer-motion";
import { FlaskConical, HeartPulse, Sparkles, ScanLine } from "lucide-react";

const pillars = [
  {
    icon: ScanLine,
    title: "Assess First",
    desc: "Movement screens, posture analysis, and mobility audits drive every plan.",
  },
  {
    icon: FlaskConical,
    title: "Evidence-Based",
    desc: "Methods grounded in biomechanics, motor control, and sports science.",
  },
  {
    icon: HeartPulse,
    title: "Joint-Friendly",
    desc: "Build strength without compromising joint health. Built for the long game.",
  },
  {
    icon: Sparkles,
    title: "Sustainable Results",
    desc: "Habits and outcomes that last beyond the session: fat loss, strength, posture.",
  },
];

export const Philosophy = () => {
  return (
    <section
      id="method"
      data-testid="philosophy-section"
      className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">The Method</div>
            <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-[-0.02em] text-white">
              Correct movement creates <span className="text-gradient-blue">lasting results.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-6 text-lg text-zinc-300/90 leading-relaxed"
          >
            Corrective Motion is built on four principles that transform movement quality into
            measurable, sustainable results for individuals across all age groups and fitness levels.
          </motion.p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`pillar-card-${i}`}
              className="group relative rounded-2xl p-7 glass glass-hover overflow-hidden"
            >
              <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#00E5FF]/0 group-hover:bg-[#00E5FF]/10 blur-2xl transition-colors duration-500"></div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0055FF]/20 to-[#00E5FF]/20 border border-white/10 flex items-center justify-center">
                <p.icon className="w-5 h-5 text-[#00E5FF]" />
              </div>
              <div className="mt-5 font-display text-xl font-bold text-white">{p.title}</div>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
              <div className="mt-6 text-xs font-mono text-zinc-600">0{(i + 1).toString()}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Philosophy;
